import {
  isSequenceApiError,
  isSequenceApiErrorResponse,
  useMutation,
  useQuery
} from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  DateInputControl,
  SettingsFormControl,
  SettingsGroup,
  Switch,
  useNotifications
} from '@sequencehq/core-components'
import { add, apiDatesAdapters, startOfDay } from '@sequencehq/utils/dates'
import { useLiveState } from 'hooks/useLiveState'
import { useState } from 'react'
import { BASE_INPUT_FIELD_STYLES } from 'SettingsV2/settings.constants'

const dateFromApi = (apiDate: string | null) => {
  if (apiDate == null) {
    return new Date()
  }

  return apiDatesAdapters.fromApi(apiDate) ?? new Date()
}

export const AccountingLockDateSettings = () => {
  const { displayNotification } = useNotifications()

  const accountSettings = useQuery(
    dashboard20240730Client.getSequenceAccountSettings
  )

  const [accountingLockDate, setAccountingLockDate] = useLiveState(
    accountSettings.data?.accountingLockDate ?? null
  )
  const [lockDateResetKey, setLockDateResetKey] = useState(0)

  const accountSettingsMutator = useMutation(
    dashboard20240730Client.putSequenceAccountSettings,
    {
      onSuccess() {
        displayNotification('Accounting lock date updated', {
          type: 'success'
        })
      },
      onError(error) {
        const description =
          isSequenceApiError(error) &&
          isSequenceApiErrorResponse(error.response)
            ? error.response.error.errors.at(0)?.message
            : undefined

        displayNotification('Accounting lock date failed to update', {
          type: 'error',
          description
        })
      }
    }
  )

  const controlsDisabled =
    !accountSettings.data || accountSettingsMutator.isPending
  const today = startOfDay(new Date())
  const minDate = add(today, { years: -10 })
  const maxDate = add(today, { years: 10 })

  const mutateAccountingLockDate = (newDate: string | null) => {
    const currentAccountSettings = accountSettings.data

    if (
      !currentAccountSettings ||
      accountSettingsMutator.isPending ||
      newDate === accountingLockDate
    ) {
      return
    }

    const previousDate = accountingLockDate
    setAccountingLockDate(newDate)
    accountSettingsMutator.mutate(
      {
        enabledCurrencies: currentAccountSettings.enabledCurrencies,
        accountingLockDate: newDate
      },
      {
        onError: () => {
          setAccountingLockDate(previousDate)
          setLockDateResetKey(prev => prev + 1)
        }
      }
    )
  }

  const handleDateChange = (newDate: Date) => {
    if (newDate < minDate || newDate > maxDate) {
      displayNotification('Lock date must be within ten years of today', {
        type: 'error'
      })
      setLockDateResetKey(prev => prev + 1)
      return
    }

    mutateAccountingLockDate(apiDatesAdapters.toApi(newDate))
  }

  const handleToggleAccountingLockDate = (enabled: boolean) => {
    mutateAccountingLockDate(
      enabled ? apiDatesAdapters.toApi(new Date()) : null
    )
  }

  return (
    <SettingsGroup
      title="Accounting lock date"
      footer="Locks accounting activity before this date. Invoices and credit notes must be dated on or after it to be finalised or sent, and revenue recognition journals are not posted before it."
      isLoading={accountSettings.isLoading}
    >
      <SettingsFormControl
        label="Set accounting lock date"
        htmlFor="enable-accounting-lock-date"
      >
        <Switch
          size="md"
          id="enable-accounting-lock-date"
          as="div"
          data-testid="accountSettings.accountingLockDate.enabled"
          isChecked={accountingLockDate != null}
          disabled={controlsDisabled}
          onChange={handleToggleAccountingLockDate}
        />
      </SettingsFormControl>

      {accountingLockDate != null && (
        <SettingsFormControl
          label="Accounting lock date"
          htmlFor="accounting-lock-date"
          showBorderBottom={false}
        >
          <DateInputControl
            key={lockDateResetKey}
            inputProps={{
              id: 'accounting-lock-date',
              'data-testid': 'accountSettings.accountingLockDate.date',
              height: '32px',
              width: BASE_INPUT_FIELD_STYLES.width,
              isDisabled: controlsDisabled
            }}
            onChange={handleDateChange}
            value={dateFromApi(accountingLockDate)}
            minDate={minDate}
            maxDate={maxDate}
            popoverTestId="accountSettings.accountingLockDate.calendar"
          />
        </SettingsFormControl>
      )}
    </SettingsGroup>
  )
}
